import React,{ useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import mainAxios from '../../services/mainAxios';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Checkbox } from 'primereact/checkbox';


const useDownloadPurchInvoices = () => {
  const { t } = useTranslation('translations');
  const [selectedRows, setSelectedRows] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const downloadSelectedInvoices = useCallback(async () => {
    if (!selectedRows || selectedRows.length === 0) {
      toast.warning(t('invoice.noSelected'), { containerId: 'all_toast' });
      return;
    }
    setActionLoading(true);
    try {
      const body = {
        object: 'PurchasePDFs',
        ids: (selectedRows || []).map((s) => s?.attachmentId),
      };

      const res = await mainAxios.post('/endpoint/v2/download-multiple', body, {
        responseType: 'arraybuffer',
      });

      const headers = res.headers || {};
      const contentType = (headers['content-type'] || '').toLowerCase();
      const contentDisposition = headers['content-disposition'] || '';

      const isBinary =
        contentType.includes('application/octet-stream') ||
        contentType.includes('zip') ||
        /filename=.*\.zip/i.test(contentDisposition);

      if (isBinary) {
        let filename = moment().format('DD/MM/YYYY') + '.zip';
        const fnMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)/i);
        if (fnMatch && fnMatch[1]) {
          try {
            filename = decodeURIComponent(fnMatch[1]);
          } catch (e) {
            filename = fnMatch[1];
          }
        }

        const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      try {
        const text = new TextDecoder().decode(res.data);
        const json = JSON.parse(text);
        const msg = json?.message || json?.error || t('common.error');
        toast.error(msg, { containerId: 'all_toast' });
      } catch (e) {
        toast.error(t('common.unexpectedResponse'), { containerId: 'all_toast' });
      }
    } catch (err) {
      const errRes = err?.response;
      if (errRes && errRes.data) {
        try {
          const text = new TextDecoder().decode(errRes.data);
          const json = JSON.parse(text);
          toast.error(json?.message || json?.error || t('common.error'), { containerId: 'all_toast' });
        } catch (e) {
          toast.error(t('common.error'), { containerId: 'all_toast' });
        }
      } else {
        toast.error(t('common.error'), { containerId: 'all_toast' });
      }
    } finally {
      setActionLoading(false);
    }
  }, [selectedRows, t]);

  const downloadCheckboxBody = useCallback(
    (invoice) => {
      if (!invoice?.attachmentId) return null;
      return (
        <Checkbox
          onChange={() => {
            setSelectedRows((state) => {
              const _state = [...state];
              const idx = _state.findIndex((s) => s?.attachmentId === invoice?.attachmentId);
              if (idx >= 0) {
                _state.splice(idx, 1);
                return _state;
              }
              _state.push({ id: invoice?.id, attachmentId: invoice?.attachmentId });
              return _state;
            });
          }}
          checked={!!selectedRows.find((s) => s?.attachmentId === invoice?.attachmentId)}
        />
      );
    },
    [selectedRows]
  );

  const downloadMultiFilter = useCallback(
    (invoices) => {
      const downloadable = (invoices || []).filter((i) => i?.attachmentId);
      if (!downloadable.length) return null;

      const handleCheckAll = (e) => {
        if (e.checked) {
          const _selected = downloadable.map((inv) => ({
            id: inv?.id,
            attachmentId: inv?.attachmentId,
          }));
          setSelectedRows(_selected);
        } else {
          setSelectedRows([]);
        }
      };

      const allSelected = downloadable.every((inv) =>
        selectedRows.some((s) => s?.attachmentId === inv?.attachmentId)
      ) && downloadable.length > 0;

      return (
        <Checkbox
          onChange={handleCheckAll}
          checked={allSelected}
        />
      );
    },
    [selectedRows]
  );

  const clearSelectedRows = (
    <button
      className="btn btn-sm btn-outline-primary rounded-pill mr-3"
      disabled={actionLoading}
      onClick={() => setSelectedRows([])}
    >
      {t('common.clear')}
    </button>
  );

  const downloadButtonsRight = selectedRows.length > 0 && (
    <div className="d-flex align-items-center">
      <button
        className="btn btn-sm btn-outline-primary rounded-pill ml-2 mr-2"
        onClick={downloadSelectedInvoices}
        disabled={actionLoading || selectedRows.length === 0}
      >
        {`${t('invoice.downloadSelected')} (${selectedRows.length})`}
      </button>
      {clearSelectedRows}
    </div>
  );

  return {
    selectedRows,
    setSelectedRows,
    actionLoading,
    downloadSelectedInvoices,
    downloadCheckboxBody,
    downloadMultiFilter,
    downloadButtonsRight,
  };
};

export default useDownloadPurchInvoices;